import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CircleNotch, Users } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogIcon,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { MemberEmailsField } from "@/components/session-members/MemberEmailsField";
import { apiSend } from "@/lib/api-error";
import { sessionMembersQuery } from "./session-members-query";
import { normalizeMemberEmails } from "@/lib/session-members";
import { formatDateWithOptionsBs } from "@/lib/format";
import { formatTimeBs } from "@/lib/timezone";
import { toast } from "@/lib/toast";
import type { SessionMembersResponse } from "@/types";
import type { CalendarSession } from "./types";

/** Terapeut/admin mijenja emailove ostalih članova seanse (usluga sa više članova). */
export function SessionMembersDialog({
  session,
  onClose,
  onSaved,
}: {
  session: CalendarSession;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { data, isLoading } = useQuery(sessionMembersQuery(session.id));
  const [emails, setEmails] = useState<string[]>([]);
  useEffect(() => {
    if (data) setEmails(data.emails);
  }, [data]);

  const save = useMutation({
    mutationFn: (list: string[]) =>
      apiSend<SessionMembersResponse>(`/api/sessions/${session.id}/members`, "PUT", { emails: list }, "Greška pri spremanju članova"),
    onSuccess: () => {
      toast.success("Članovi spremljeni.");
      onSaved();
      onClose();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Greška pri spremanju članova"),
  });

  const handleSave = () => {
    if (!data) return;
    const members = normalizeMemberEmails(emails, { maxExtra: data.maxExtra });
    if (!members.ok) {
      toast.error(members.error);
      return;
    }
    save.mutate(members.emails);
  };

  const dateFormatted = formatDateWithOptionsBs(session.scheduledAt, {
    weekday: "short", day: "numeric", month: "long", timeZone: "Europe/Sarajevo",
  });

  return (
    <Dialog open onClose={onClose} className="sm:max-w-md">
      <DialogHeader onClose={onClose}>
        <div className="flex items-center gap-3">
          <DialogIcon>
            <Users className="size-5" />
          </DialogIcon>
          <div>
            <DialogTitle>Članovi seanse</DialogTitle>
            <DialogDescription className="mt-0 text-xs">
              Novi član plaćene seanse odmah dobija potvrdu sa Meet linkom
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      <DialogBody className="space-y-4">
        <div className="rounded-xl bg-muted/30 p-3 text-sm">
          <p className="font-medium text-foreground">{session.clientName}</p>
          <p className="text-muted-foreground">{dateFormatted} · {formatTimeBs(session.scheduledAt)}</p>
        </div>
        {isLoading || !data ? (
          <CircleNotch className="size-5 animate-spin text-muted-foreground" />
        ) : (
          <MemberEmailsField
            label="Ostali članovi"
            value={emails}
            onChange={setEmails}
            maxExtra={data.maxExtra}
            readOnly={!data.editable}
          />
        )}
      </DialogBody>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Zatvori
        </Button>
        {data?.editable && (
          <Button onClick={handleSave} disabled={save.isPending}>
            {save.isPending && <CircleNotch className="size-4 animate-spin" />}
            Spremi
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
}
